import { createHOC } from 'vue-hoc';
import gql from 'graphql-tag';
import withUser from '~lib/common/hoc/withUser';

import auth from '~lib/common/providers/auth';
import casesService from '~lib/common/providers/casesService';

import UserCaseSelector from './UserCaseSelector';
import parseGroupMetadata from '../../../utils/parseGroupMetadata';
import envBaseUrl from '../../../utils/envBaseUrl';
import envUsersService from '../../../utils/envUsersService';

const options = {
  name: 'UserCaseSelectorContainer',
  props: {
    user: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
    }
  },
  data() {
    return {
      state: {
        loading: false,
        copying: false,
        fromEnv: 'dev',
        toEnv: 'dev',
        fromOrgs: [],
        toOrgs: [],
        fromCases: [],
        fromUsers: [],
        toUsers: [],
        error: null,
      },
    };
  },
  async created() {
    if (!(await auth.isAuthenticated())) {
      this.$router.replace({ name: 'login' });
      return;
    }
    this.updateFromOrgs();
  },
  methods: {
    async updateFromOrgs() {
      this.state.fromOrgs = await this.getOrgsFromEnv(this.state.fromEnv);
    },
    async getOrgsFromEnv(env) {
      try {
        this.state.loading = true;
        const orgs = await envUsersService(env).getOrgs();
        return orgs.map((group) => ({
          ...group,
          metadata: parseGroupMetadata(group),
        })).filter(group => !group.metadata.parent);
      } catch (e) {
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }
    },
  },
};

const renderWith = {
  props: {
    loading() {
      return this.loading || this.state.loading;
    },
    copying() {
      return this.state.copying;
    },
    fromEnv() {
      return this.state.fromEnv;
    },
    toEnv() {
      return this.state.toEnv;
    },
    fromOrgs() {
      return this.state.fromOrgs;
    },
    toOrgs() {
      return this.state.toOrgs;
    },
    fromCases() {
      return this.state.fromCases;
    },
    fromUsers() {
      return this.state.fromUsers;
    },
    toUsers() {
      return this.state.toUsers;
    },
    error() {
      return this.state.error;
    },
  },
  listeners: {
    async updateFromEnv(env) {
      this.state.fromEnv = env;
      // Reload orgs from the newly selected environment.
      this.state.fromOrgs = await this.getOrgsFromEnv(env);
    },
    async updateFromCases(selectedOrg, selectedUser) {
      if (!selectedOrg) {
        return;
      }
      if (!selectedUser) {
        return;
      }
      try {
        this.state.loading = true;
        this.state.error = null;
        const { fromEnv } = this.state;
        const authResult = await envUsersService(fromEnv).loginAs(
          selectedOrg._id,
          selectedUser.email
        );
        // The selected From user may be a SUPER_ADMIN, whose token trips the
        // cases service's unfiltered "ignore" path and returns every org's
        // cases. Pass an explicit withAccess scope — honored for super-admin
        // callers (makeQueryResolver) — so the listing is always constrained to
        // the selected org. Non-super callers ignore withAccess and fall back
        // to their own token access, which the orgGroups override below already
        // scopes correctly. Built from the loginAs token, so it always reflects
        // the selected org and user.
        const ns = 'https://vrpatients.com/';
        const tokenPayload = authResult.idTokenPayload || {};
        const withAccess = {
          userId: tokenPayload.sub,
          orgId: (tokenPayload[`${ns}app_metadata`] || {}).activeOrg || selectedOrg._id,
          groups: tokenPayload[`${ns}groups`],
          role: tokenPayload[`${ns}role`],
          productType: tokenPayload[`${ns}productType`],
        };
        const response = await casesService.query({
          query: gql`
            query getCaseConfigurations($withAccess: AccessContextInput) {
              caseConfigurations(sortBy: "name", withAccess: $withAccess) {
                id
                name
              }
            }
          `,
          variables: { withAccess },
          fetchPolicy: 'network-only',
          context: {
            uri: `${envBaseUrl(fromEnv)}/graphql`,
            // Scope to the impersonated user's own org (carried by the
            // loginAs token below). Without this the signed-in user's active
            // org groups get merged server-side, getting the logged-in
            // org's cases instead of the selected org's.
            orgGroups: [],
            headers: {
              Authorization: `Bearer ${
                authResult.idToken
              }`
            }
          }
        });
        this.state.fromCases = response.data.caseConfigurations;
      } catch (e) {
        console.error(e);
        if (e.graphqlErrors && e.graphqlErrors.length) {
          this.state.error = e.graphqlErrors[0];
        }
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }
    },
    async updateFromUsers(org) {
      try {
        this.state.error = null;
        this.state.loading = true;
        const users = await envUsersService(this.state.fromEnv).getOrgMembers(org._id);
        this.state.fromUsers = users;
      } catch (e) {
        console.error(e);
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }
    },
  },
};

const UserCaseSelectorContainer = createHOC(UserCaseSelector, options, renderWith);
export default withUser()(UserCaseSelectorContainer);
